import { get } from 'services/request';

export interface IBatchJob {
  id: number;
  name: string;
  dialectId: number;
  dialectCode: string;
  ref: number;
  refName: string;
  mode: number;
  modeLabel: string;
  status: number;
  statusLabel: string;
  currentStep: number;
  totalItems: number;
  memoryTranslated: number;
  memoryProcessed: number;
  aiTranslated: number;
  aiValidated: number;
  aiRetried: number;
  aiFailed: number;
  memorySiblingsTranslated: number;
  memorySiblingsProcessed: number;
  providerBatchStep: string | null;
  providerBatchId: string | null;
  stepIngested: number;
  stepTotal: number;
  stepStartedAt: string | null;
  errorMessage: string | null;
  projectId: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface IBatchPreviewJob {
  courseId: number;
  courseName: string;
  ref: number;
  refName: string;
  count: number;
}

export interface IBatchProgressSlot {
  slot: 0 | 1;
  batchId: string;
  total: number;
  completed: number;
  failed: number;
  status: string | null;
  error: string | null;
}

export interface IBatchProgress {
  jobId: number;
  providerBatchStep: string | null;
  slots: IBatchProgressSlot[];
}

// Status constants (mirror backend TranslationBatchJob)
export const BatchJobStatus = {
  PENDING: 0,
  MEMORY_PASS: 1,
  RB_SELECTING: 2,
  PB_SELECTING: 3,
  AI_TRANSLATING: 4,
  AI_VALIDATING: 5,
  AI_RETRYING: 6,
  MEMORY_SIBLINGS: 7,
  DONE: 8,
  FAILED: 9,
  CANCELLED: 10,
} as const;

export const BatchJobMode = {
  FULL: 0,
  MEMORY_ONLY: 1,
} as const;

export const BatchJobStep = {
  MEMORY: 0,
  RB_PB: 1,
  TRANSLATE: 2,
  VALIDATE: 3,
  RETRY: 4,
  MEMORY_SIBLINGS: 5,
  DONE: 6,
} as const;

export default class TranslationBatchModel {
  render: () => void;
  jobs: IBatchJob[] = [];
  previewJobs: IBatchPreviewJob[] = [];
  loading = false;
  creating = false;
  previewing = false;
  private pollTimer: number | null = null;

  constructor(options: { render: () => void }) {
    this.render = options.render;
  }

  async loadJobs(dialectId?: number) {
    this.loading = true;
    this.render();

    const params: Record<string, unknown> = { action: 'jobs' };
    if (dialectId && dialectId > 0) {
      params.dialectId = dialectId;
    }

    this.jobs = await get<IBatchJob[]>('/translation-batch', params);
    this.loading = false;
    this.render();
  }

  async preview(dialectId: number, courseIds: number[], refs: number[], startLesson: number, endLesson: number) {
    this.previewing = true;
    this.previewJobs = [];
    this.render();

    try {
      const result = await get<{ jobs: IBatchPreviewJob[] }>('/translation-batch', {
        action: 'preview',
        dialectId,
        courseIds: courseIds.join(','),
        refs: refs.join(','),
        startLesson,
        endLesson,
      });
      this.previewJobs = result.jobs;
    } catch {
      this.previewJobs = [];
    } finally {
      this.previewing = false;
      this.render();
    }
  }

  clearPreview() {
    this.previewJobs = [];
    this.render();
  }

  async createBatch(dialectId: number, selections: { [courseId: number]: { refs: number[]; start: number; end: number } }, mode: number) {
    this.creating = true;
    this.render();

    try {
      await get<{ jobIds: number[] }>('/translation-batch', {
        action: 'create',
        dialectId,
        selections: JSON.stringify(selections),
        mode,
      });
      this.previewJobs = [];
      await this.loadJobs(dialectId);
    } finally {
      this.creating = false;
      this.render();
    }
  }

  async getProgress(jobId: number): Promise<IBatchProgress | null> {
    try {
      return await get<IBatchProgress>('/translation-batch', {
        action: 'progress',
        jobId,
      });
    } catch {
      return null;
    }
  }

  startPolling(dialectId?: number) {
    this.stopPolling();
    this.pollTimer = window.setInterval(async () => {
      const hasActive = this.jobs.some(
        (j) => j.status !== BatchJobStatus.DONE && j.status !== BatchJobStatus.FAILED && j.status !== BatchJobStatus.CANCELLED,
      );
      if (hasActive) {
        await this.loadJobs(dialectId);
      }
    }, 30000);
  }

  stopPolling() {
    if (this.pollTimer !== null) {
      window.clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }
}
